import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Breadcrumb,
  Button,
  Collapse,
  Input,
  type InputRef,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
  Spin,
} from "antd";
import { Link } from "react-router-dom";
import {
  HomeOutlined,
  PlusOutlined,
  LockOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { AdminNavBar } from "../components/AdminNavBar";
import { trackFeatureUsage } from "../lib/posthog";
import type { RoadmapItem, RoadmapItemStatus } from "../types/roadmap";
import { invokeRoadmap } from "../services/roadmapApi";
import { RoadmapCompactRow } from "../components/roadmap/RoadmapCompactRow";
import { RoadmapItemDetailModal } from "../components/roadmap/RoadmapItemDetailModal";
import {
  ROADMAP_STATUS_LABEL,
  ROADMAP_SECTION_ORDER,
  roadmapStatusTagColor,
} from "../components/roadmap/roadmapStatusConfig";

const { Title, Text } = Typography;

const SESSION_KEY = "efreightforwarding_roadmap_official_secret";

const STATUS_FILTER_OPTIONS: { value: "all" | RoadmapItemStatus; label: string }[] = [
  { value: "all", label: "All statuses" },
  ...ROADMAP_SECTION_ORDER.map((s) => ({
    value: s,
    label: ROADMAP_STATUS_LABEL[s],
  })),
];

const RoadmapAdmin: React.FC = () => {
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | RoadmapItemStatus>("all");
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [editSecret, setEditSecret] = useState(() => sessionStorage.getItem(SESSION_KEY) ?? "");
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockInput, setUnlockInput] = useState("");
  const unlockPasswordRef = useRef<InputRef>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const officialUnlocked = editSecret.length > 0;

  const loadItems = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("roadmap_items")
      .select("*, roadmap_subtasks(*), roadmap_company_questions(*)")
      .order("sort_order", { ascending: true });

    if (error) {
      message.error("Failed to load roadmap: " + error.message);
      setItems([]);
      setLoading(false);
      return;
    }

    const rows = (data as RoadmapItem[]) ?? [];
    setItems(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadItems();
    trackFeatureUsage("admin_roadmap_view");
  }, [loadItems]);

  useEffect(() => {
    if (selectedItemId && !items.some((i) => i.id === selectedItemId)) {
      setSelectedItemId(null);
    }
  }, [items, selectedItemId]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => a.sort_order - b.sort_order),
    [items]
  );

  const filtered = useMemo(() => {
    let list = sorted;
    if (statusFilter !== "all") {
      list = list.filter((i) => i.status === statusFilter);
    }
    if (priorityOnly) {
      list = list.filter((i) => i.is_prioritized);
    }
    return list;
  }, [sorted, statusFilter, priorityOnly]);

  const statusCounts = useMemo(() => {
    const counts: Record<RoadmapItemStatus, number> = {
      incoming: 0,
      planned: 0,
      in_progress: 0,
      done: 0,
      wont_do: 0,
    };
    items.forEach((i) => {
      counts[i.status] += 1;
    });
    return counts;
  }, [items]);

  /** Groups visible items by status (section order: done → … → incoming → won't do last) */
  const groupedSections = useMemo(() => {
    const statuses: RoadmapItemStatus[] =
      statusFilter === "all" ? ROADMAP_SECTION_ORDER : [statusFilter];
    return statuses
      .map((status) => ({
        status,
        items: filtered.filter((i) => i.status === status),
      }))
      .filter((g) => g.items.length > 0);
  }, [filtered, statusFilter]);

  const selectedItem = useMemo(
    () => (selectedItemId ? items.find((i) => i.id === selectedItemId) ?? null : null),
    [items, selectedItemId]
  );

  const moveItem = async (id: string, dir: "up" | "down") => {
    const order = [...sorted];
    const idx = order.findIndex((i) => i.id === id);
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    try {
      await invokeRoadmap({
        action: "reorder_official",
        itemIds: order.map((i) => i.id),
        editSecret,
      });
      message.success("Order updated");
      await loadItems();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Reorder failed");
    }
  };

  const togglePriorityFor = async (itemId: string) => {
    try {
      await invokeRoadmap({ action: "toggle_priority", itemId });
      await loadItems();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Update failed");
    }
  };

  const confirmUnlock = () => {
    const s = unlockInput.trim();
    if (!s) {
      message.warning("Enter the edit secret");
      return;
    }
    sessionStorage.setItem(SESSION_KEY, s);
    setEditSecret(s);
    setUnlockInput("");
    setUnlockOpen(false);
    message.success("Official edits unlocked for this session");
    trackFeatureUsage("admin_roadmap_unlock");
  };

  const lockOfficial = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setEditSecret("");
    message.info("Official edits locked");
  };

  const submitCreateRequest = async () => {
    const t = createTitle.trim();
    if (!t) {
      message.warning("Title required");
      return;
    }
    setCreateSubmitting(true);
    try {
      await invokeRoadmap({
        action: "create_request",
        title: t,
        description: createDescription.trim(),
      });
      message.success("Request added");
      setCreateOpen(false);
      setCreateTitle("");
      setCreateDescription("");
      await loadItems();
      trackFeatureUsage("admin_roadmap_create_request");
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setCreateSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Link to="/admin">
            <HomeOutlined /> Admin
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Roadmap</Breadcrumb.Item>
      </Breadcrumb>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Roadmap
          </Title>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              New request
            </Button>
            {officialUnlocked ? (
              <Button icon={<LockOutlined />} onClick={lockOfficial}>
                Lock official edits
              </Button>
            ) : (
              <Button icon={<UnlockOutlined />} onClick={() => setUnlockOpen(true)}>
                Unlock official edits
              </Button>
            )}
          </Space>
        </div>

        {!loading && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              background: "#fafafa",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
              maxWidth: 960,
            }}
          >
            <Text type="secondary" style={{ marginRight: 4 }}>
              By status
            </Text>
            <Tag
              style={{
                cursor: "pointer",
                margin: 0,
                ...(statusFilter === "all"
                  ? { boxShadow: "0 0 0 2px #1677ff", borderRadius: 4 }
                  : {}),
              }}
              onClick={() => setStatusFilter("all")}
            >
              All <strong>{items.length}</strong>
            </Tag>
            {ROADMAP_SECTION_ORDER.map((s) => (
              <Tag
                key={s}
                color={roadmapStatusTagColor(s)}
                style={{
                  cursor: "pointer",
                  margin: 0,
                  ...(statusFilter === s ? { boxShadow: "0 0 0 2px #1677ff", borderRadius: 4 } : {}),
                }}
                onClick={() => setStatusFilter(s)}
              >
                {ROADMAP_STATUS_LABEL[s]} <strong>{statusCounts[s]}</strong>
              </Tag>
            ))}
          </div>
        )}

        <Space wrap align="center">
          <Text>Status</Text>
          <Select
            style={{ minWidth: 160 }}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={STATUS_FILTER_OPTIONS}
          />
          <Space>
            <Text>Prioritised only</Text>
            <Switch checked={priorityOnly} onChange={setPriorityOnly} />
          </Space>
        </Space>

        {loading ? (
          <Spin />
        ) : (
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              overflow: "hidden",
              maxWidth: 960,
            }}
          >
            {filtered.length === 0 ? (
              <div style={{ padding: 16 }}>
                <Text type="secondary">No items match filters.</Text>
              </div>
            ) : (
              groupedSections.map(({ status: groupStatus, items: groupItems }) => {
                const sectionHeader = (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 12px",
                      background: "#f5f5f5",
                      borderBottom: "1px solid #e8e8e8",
                    }}
                  >
                    <Tag color={roadmapStatusTagColor(groupStatus)} style={{ margin: 0 }}>
                      {ROADMAP_STATUS_LABEL[groupStatus]}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {groupItems.length} {groupItems.length === 1 ? "task" : "tasks"}
                    </Text>
                  </div>
                );

                const rows = groupItems.map((item) => {
                  const idx = sorted.findIndex((i) => i.id === item.id);
                  return (
                    <RoadmapCompactRow
                      key={item.id}
                      item={item}
                      onOpen={() => setSelectedItemId(item.id)}
                      onTogglePriority={(e) => {
                        e.stopPropagation();
                        void togglePriorityFor(item.id);
                      }}
                      officialUnlocked={officialUnlocked}
                      canMoveUp={idx > 0}
                      canMoveDown={idx >= 0 && idx < sorted.length - 1}
                      onMoveUp={(e) => {
                        e.stopPropagation();
                        void moveItem(item.id, "up");
                      }}
                      onMoveDown={(e) => {
                        e.stopPropagation();
                        void moveItem(item.id, "down");
                      }}
                    />
                  );
                });

                const collapseWontDo = groupStatus === "wont_do" && statusFilter === "all";
                if (collapseWontDo) {
                  return (
                    <Collapse
                      key={groupStatus}
                      bordered={false}
                      ghost
                      defaultActiveKey={[]}
                      expandIconPosition="end"
                      style={{ background: "transparent" }}
                      items={[
                        {
                          key: "wont_do",
                          label: sectionHeader,
                          styles: {
                            header: { margin: 0, padding: 0, background: "transparent" },
                            body: { padding: 0 },
                          },
                          children: <>{rows}</>,
                        },
                      ]}
                    />
                  );
                }

                return (
                  <div key={groupStatus}>
                    {sectionHeader}
                    {rows}
                  </div>
                );
              })
            )}
          </div>
        )}
      </Space>

      <RoadmapItemDetailModal
        open={selectedItemId !== null && selectedItem !== null}
        item={selectedItem}
        onClose={() => setSelectedItemId(null)}
        editSecret={editSecret}
        officialUnlocked={officialUnlocked}
        onRefresh={loadItems}
        canMoveUp={
          selectedItem ? sorted.findIndex((i) => i.id === selectedItem.id) > 0 : false
        }
        canMoveDown={
          selectedItem
            ? sorted.findIndex((i) => i.id === selectedItem.id) < sorted.length - 1 &&
              sorted.findIndex((i) => i.id === selectedItem.id) >= 0
            : false
        }
        onMoveUp={() => selectedItem && void moveItem(selectedItem.id, "up")}
        onMoveDown={() => selectedItem && void moveItem(selectedItem.id, "down")}
      />

      <Modal
        title="Unlock official edits"
        open={unlockOpen}
        afterOpenChange={(open) => {
          if (open) {
            unlockPasswordRef.current?.focus();
          }
        }}
        onCancel={() => {
          setUnlockOpen(false);
          setUnlockInput("");
        }}
        onOk={confirmUnlock}
        okText="Unlock"
      >
        <Input.Password
          ref={unlockPasswordRef}
          placeholder="Roadmap edit secret"
          value={unlockInput}
          onChange={(e) => setUnlockInput(e.target.value)}
          onPressEnter={confirmUnlock}
        />
      </Modal>

      <Modal
        title="New roadmap request"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setCreateTitle("");
          setCreateDescription("");
        }}
        onOk={submitCreateRequest}
        confirmLoading={createSubmitting}
        okText="Submit"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Input
            placeholder="Title"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
          />
          <Input.TextArea
            rows={3}
            placeholder="Description (optional)"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default RoadmapAdmin;
